import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon, ChevronRightIcon, ClockIcon,
  BanknotesIcon, CalendarDaysIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ty-root * { box-sizing:border-box; }
  .ty-root { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tyFade .4s ease both; }
  @keyframes tyFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
  .ty-back { display:inline-flex; align-items:center; gap:6px; font-size:.78rem; font-weight:600; color:#7880a4; cursor:pointer; margin-bottom:1.4rem; border:none; background:none; font-family:'Sora',sans-serif; padding:0; transition:color .15s; }
  .ty-back:hover { color:#3b6ef8; }
  .ty-back svg { width:14px; height:14px; stroke-width:2.5; }
  .ty-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.75rem; flex-wrap:wrap; gap:1rem; }
  .ty-teacher { display:flex; align-items:center; gap:12px; flex:1; min-width:0; }
  .ty-avatar  { width:46px; height:46px; border-radius:13px; background:#ecfdf5; color:#10c98e; display:flex; align-items:center; justify-content:center; font-size:.95rem; font-weight:700; text-transform:uppercase; flex-shrink:0; }
  .ty-name    { font-size:1.3rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .ty-empid   { font-family:'JetBrains Mono',monospace; font-size:.72rem; color:#9094a8; margin-top:2px; }
  .ty-controls { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .year-nav { display:flex; align-items:center; gap:8px; background:white; border:1px solid #e8ebf4; border-radius:12px; padding:6px 10px; }
  .year-btn  { width:30px; height:30px; border-radius:8px; border:1px solid #e4e7f0; background:white; color:#6b7194; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
  .year-btn:hover { background:#eef2ff; border-color:#3b6ef8; color:#3b6ef8; }
  .year-btn:disabled { opacity:.35; cursor:not-allowed; }
  .year-btn svg { width:14px; height:14px; stroke-width:2.5; }
  .year-label { font-family:'JetBrains Mono',monospace; font-size:.9rem; font-weight:600; color:#1a1d2e; min-width:52px; text-align:center; }
  .req-banner { background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:9px 14px; font-size:.76rem; color:#9a3412; font-weight:600; margin-bottom:1.2rem; }
  .month-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; }
  .month-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem; position:relative; overflow:hidden; transition:transform .18s,box-shadow .18s,border-color .18s; animation:tyFade .4s ease both; }
  .month-card.clickable { cursor:pointer; }
  .month-card.clickable:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(59,110,248,.1); border-color:#c8d5fd; }
  .month-card.future { opacity:.5; pointer-events:none; }
  .mc-top    { display:flex; align-items:center; justify-content:space-between; margin-bottom:.9rem; gap:6px; flex-wrap:wrap; }
  .mc-name   { font-size:.88rem; font-weight:700; color:#0f1123; }
  .mc-badge  { display:inline-flex; align-items:center; gap:4px; font-size:.65rem; font-weight:700; padding:3px 8px; border-radius:99px; white-space:nowrap; }
  .mc-badge svg { width:11px; height:11px; stroke-width:2.5; }
  .mc-badge.finalized { background:#ecfdf5; color:#10c98e; }
  .mc-badge.pending   { background:#f0f2f7; color:#9094a8; }
  .mc-badge.paid      { background:#eef2ff; color:#3b6ef8; }
  .mc-badge.unpaid    { background:#fef2f2; color:#ef4444; }
  .mc-days   { display:flex; align-items:center; gap:5px; font-size:.72rem; color:#9094a8; margin-bottom:6px; }
  .mc-days svg { width:13px; height:13px; stroke-width:1.8; }
  .mc-salary { font-family:'JetBrains Mono',monospace; font-size:.82rem; font-weight:600; color:#10c98e; }
  .mc-salary.empty { color:#c0c5dc; font-weight:400; }
  .mc-finalize-btn { margin-top:10px; width:100%; padding:7px; border:1.5px solid #fde68a; border-radius:9px; background:#fffbeb; color:#d97706; font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; cursor:pointer; transition:all .15s; }
  .mc-finalize-btn:hover { background:#d97706; color:white; border-color:#d97706; }
  .ty-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; font-size:.88rem; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  /* Modal */
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.65); backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; }
  .mo { background:white; border-radius:20px; width:100%; max-width:420px; max-height:92vh; display:flex; flex-direction:column; box-shadow:0 25px 60px rgba(0,0,0,.25); animation:moIn .25s ease; overflow:hidden; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; flex-shrink:0; }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .mo-x { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-x svg { width:14px; height:14px; stroke-width:2; }
  .mo-bd { padding:20px 22px; overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:12px; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; flex-shrink:0; }
  .ff { display:flex; flex-direction:column; gap:5px; }
  .fl { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fi,.fta { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .fi:focus,.fta:focus { border-color:#3b6ef8; background:white; }
  .fta { resize:vertical; min-height:60px; }
  .btn-save  { padding:9px 20px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-save:disabled { opacity:.6; cursor:not-allowed; }
  .btn-ghost { padding:9px 18px; border:none; border-radius:10px; background:#f0f2f7; color:#6b7194; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  @media (max-width:900px)  { .month-grid { grid-template-columns:repeat(3,1fr); } }
  @media (max-width:600px)  { .month-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:380px)  { .month-grid { grid-template-columns:1fr; } }
`;

const initials = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase() || '?';
const fmt      = n => `Rs. ${(n||0).toLocaleString('en-PK',{minimumFractionDigits:0,maximumFractionDigits:0})}`;

export default function CoordStaffSalaryYear() {
  const { staffId }     = useParams();
  const [searchParams]  = useSearchParams();
  const role     = searchParams.get('role') || 'teacher';
  const isPortal = searchParams.get('isPortal') !== 'false';
  const navigate = useNavigate();
  const now      = new Date();

  const [year,       setYear]       = useState(now.getFullYear());
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null); // month obj
  const [myUserId,   setMyUserId]   = useState(null);
  const [form,       setForm]       = useState({ netSalary:'', daysPresent:'', note:'' });
  const [saving,     setSaving]     = useState(false);

  useEffect(() => {
    api.get('/coordinator/me').then(r => setMyUserId(String(r.data?._id||'')||String(r.data?.userId||''))).catch(()=>{});
  }, []);
  useEffect(() => { fetchYear(); }, [staffId, year]);

  const fetchYear = async () => {
    try {
      setLoading(true);
      const { data: res } = await api.get(`/coordinator/staff-salary-detail/${staffId}?year=${year}&role=${role}&isPortal=${isPortal}`);
      setData(res);
    } catch { toast.error('Failed to load'); }
    finally   { setLoading(false); }
  };

  const isFuture = (monthStr) => {
    const [y, m] = monthStr.split('-').map(Number);
    return new Date(y, m-1, 1) > new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const openFinalize = (m, e) => {
    e.stopPropagation();
    setForm({ month: m.month, netSalary: m.netSalary || '', daysPresent: m.daysPresent || '', note: '' });
    setModal(m);
  };

  const submitRequest = async () => {
    setSaving(true);
    try {
      await api.post('/coordinator/request/salary-finalize', {
        staffId, staffName: data?.teacher?.name || '—',
        isPortal, month: modal.month,
        netSalary:   form.netSalary,
        daysPresent: form.daysPresent,
        note:        form.note,
      });
      toast.success('Salary finalize request submitted');
      setModal(null);
    } catch { toast.error('Failed to submit'); }
    finally { setSaving(false); }
  };

  return (
    <div className="ty-root"><style>{style}</style>
      <button className="ty-back" onClick={() => navigate('/coordinator/staff-salary')}>
        <ChevronLeftIcon />Back to Staff
      </button>

      <div className="ty-header">
        {data && (
          <div className="ty-teacher">
            <div className="ty-avatar">{initials(data.teacher?.name)}</div>
            <div>
              <div className="ty-name">{data.teacher?.name}</div>
              {data.teacher?.employeeId && <div className="ty-empid">{data.teacher.employeeId}</div>}
            </div>
          </div>
        )}
        <div className="ty-controls">
          <div className="year-nav">
            <button className="year-btn" onClick={() => setYear(y => y-1)}><ChevronLeftIcon /></button>
            <span className="year-label">{year}</span>
            <button className="year-btn" onClick={() => setYear(y => y+1)} disabled={year >= now.getFullYear()}><ChevronRightIcon /></button>
          </div>
        </div>
      </div>

      <div className="req-banner">👁 View only — use the Finalize button on each month to submit a request for admin approval.</div>

      {loading ? (
        <div className="ty-loading"><div className="spinner"/>Loading…</div>
      ) : (
        <div className="month-grid">
          {data?.months?.map((m, i) => (
            <div key={m.month}
              className={`month-card${!isFuture(m.month) ? ' clickable' : ' future'}`}
              style={{ animationDelay:`${i*.03}s` }}
              onClick={() => !isFuture(m.month) && navigate(`/coordinator/staff-salary-year/${staffId}/${m.month}?role=${role}&isPortal=${isPortal}`)}>
              <div className="mc-top">
                <div className="mc-name">{m.label}</div>
                {m.isFinalized ? (
                  <span className={`mc-badge ${m.status==='paid' ? 'paid' : 'unpaid'}`}>
                    {m.status==='paid' ? '✓ Paid' : '● Unpaid'}
                  </span>
                ) : isFuture(m.month) ? (
                  <span className="mc-badge pending">Upcoming</span>
                ) : (
                  <span className="mc-badge pending"><ClockIcon />Pending</span>
                )}
              </div>
              <div className="mc-days"><CalendarDaysIcon />{m.daysPresent} day{m.daysPresent!==1?'s':''} present</div>
              <div className={`mc-salary${!m.netSalary?' empty':''}`}>{m.netSalary ? fmt(m.netSalary) : 'Not finalized'}</div>
              {!isFuture(m.month) && String(staffId) !== String(myUserId) && (
                <button className="mc-finalize-btn" onClick={(e) => { e.stopPropagation(); openFinalize(m, e); }}>
                  Request Finalize
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Finalize Request Modal */}
      {modal && createPortal(
        <div className="mb" onClick={() => setModal(null)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">💵 Request Salary Finalization</div>
              <button className="mo-x" onClick={() => setModal(null)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:9,padding:'8px 12px',fontSize:'.76rem',color:'#9a3412',fontWeight:600}}>
                <strong>{data?.teacher?.name}</strong> · {modal.month}
              </div>
              <div style={{background:'#f0fdfa',border:'1px solid #a7f3d0',borderRadius:9,padding:'8px 12px',fontSize:'.74rem',color:'#065f46'}}>
                This will be submitted as a request — admin must approve before salary is finalized.
              </div>
              <div className="ff">
                <label className="fl">Net Salary (Rs.)</label>
                <input type="number" className="fi" value={form.netSalary} onChange={e => setForm(p=>({...p,netSalary:e.target.value}))} placeholder="0" />
              </div>
              <div className="ff">
                <label className="fl">Days Present</label>
                <input type="number" className="fi" value={form.daysPresent} onChange={e => setForm(p=>({...p,daysPresent:e.target.value}))} placeholder="0" />
              </div>
              <div className="ff">
                <label className="fl">Note (optional)</label>
                <textarea className="fta" value={form.note} onChange={e => setForm(p=>({...p,note:e.target.value}))} placeholder="Any notes for admin…" />
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-save" onClick={submitRequest} disabled={saving}>
                {saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}